import { createContext, useContext, useEffect } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';

interface AnalyticsContextValue {
  trackClick: (eventName: string, metadata?: Record<string, any>) => void;
  trackFunnelStep: (funnelName: string, stepNumber: number, stepName: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue>({
  trackClick: () => {},
  trackFunnelStep: () => {},
});

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const analytics = useAnalytics();

  // Auto-instrumentation: any element with [data-analytics="event_name"] is tracked on click.
  // Optional [data-analytics-meta='{"k":"v"}'] adds metadata.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest<HTMLElement>('[data-analytics]');
      if (!el) return;
      const name = el.getAttribute('data-analytics');
      if (!name) return;
      let meta: Record<string, any> = {};
      const rawMeta = el.getAttribute('data-analytics-meta');
      if (rawMeta) {
        try { meta = JSON.parse(rawMeta); } catch { /* ignore malformed JSON */ }
      }
      analytics.trackClick(name, meta);
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true } as any);
  }, [analytics]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalyticsContext() {
  return useContext(AnalyticsContext);
}
